/**
 * This class provides methods to show and hide validation messages for form fields and the form itself.
 */
export default class FormValidationMessagesClass {
    constructor() {
        this.formErrorMessageClass = 'form-error-message';
        this.formSuccessMessageClass = 'form-success-message';
        this.fieldErrorMessageClass = 'error-message-text';
        this.hasErrorClass = 'has-error';
    }

    /**
     * Creates and inserts a message element into the DOM.
     * @param {HTMLElement} target - The target element where the message will be inserted.
     * @param {string} message - The message text to display.
     * @param {string} messageClass - The CSS class to apply to the message element.
     * @param {boolean} [insertBefore=false] - Whether to insert the message before the first child of the target.
     */
    _createAndInsertMessage(target, message, messageClass, insertBefore = false) {
        // Check for an existing message for the specific target element, instead of any element with the messageClass
        const existingMessage = target.querySelector(`.${messageClass}`);
        if (existingMessage) {
            existingMessage.innerText = message; // Update the message if it already exists
        } else {
            const messageElement = document.createElement('div');
            messageElement.className = messageClass;
            messageElement.innerText = message;
            if (insertBefore) {
                target.insertBefore(messageElement, target.firstChild);
            } else {
                target.appendChild(messageElement);
            }
        }
    }

    /**
     * Removes a message element from the DOM.
     * @param {string} messageClass - The CSS class of the message element to remove.
     */
    _removeMessage(messageClass) {
        console.log(messageClass)
        const message = document.querySelector(`.${messageClass}`);
        if (message) {
            message.remove();
        }
    }

    /**
     * Removes all instances of a message element from the DOM.
     * @param {HTMLElement} target - The target element within which the messages will be removed.
     * @param {string} messageClass - The CSS class of the message elements to remove.
     */
    _removeMessages(target, messageClass) {
        const messages = target.querySelectorAll(`.${messageClass}`);
        console.log(messages)
        messages.forEach(message => message.remove());
    }

    /**
     * Shows an error message for a specific form field.
     * @param {string} fieldID - The ID of the field to show the error message for.
     * @param {string|null} [message=null] - The error message to display.
     */
    showErrorForField(fieldID, message = null) {
        const input = document.getElementById(fieldID);
        const parent = input ? input.closest('.js-input-validation') : null;
        if (parent) {
            parent.classList.add(this.hasErrorClass);
            if (message) {
                this._createAndInsertMessage(parent, message, this.fieldErrorMessageClass);
            }
        }
    }

    /**
     * Shows an error message for the entire form and optionally for specific fields.
     * @param {string} formID - The ID of the form to show the error message for.
     * @param {string|null} [message=null] - The error message to display for the form.
     * @param {string[]|null} [fields=null] - An array of field IDs to show error messages for.
     */
    showErrorForForm(formID, message = null, fields = null) {
        const form = document.getElementById(formID);
        if (form) {
            form.classList.add('form-has-error');
            if (message) {
                this._createAndInsertMessage(form, message, this.formErrorMessageClass, true);
            }
            if (fields) {
                fields.forEach(fieldID => this.showErrorForField(fieldID));
            }
        }
    }

    /**
     * Shows a success message for the entire form.
     * @param {string} formID - The ID of the form to show the success message for.
     * @param {string|null} [message=null] - The success message to display.
     */
    showSuccessForForm(formID, message = null) {
        const form = document.getElementById(formID);
        if (form) {
            if (message) {
                this._createAndInsertMessage(form, message, this.formSuccessMessageClass, true);
            }
            this._removeMessage(this.formErrorMessageClass);
            form.classList.remove('form-has-error');
            form.querySelectorAll(`.${this.hasErrorClass}`).forEach(item => item.classList.remove(this.hasErrorClass));
        }
    }

    /**
     * Hides the error message for a specific form field.
     * @param {string} fieldID - The ID of the field to hide the error message for.
     */
    hideErrorForField(fieldID) {
        const input = document.getElementById(fieldID);
        const parent = input ? input.closest('.js-input-validation') : null;
        if (parent) {
            parent.classList.remove(this.hasErrorClass);
            this._removeMessages(parent, this.fieldErrorMessageClass); // Remove messages within the field's parent
        }
    }

    /**
     * Hides all error messages for the entire form.
     * @param {string} formID - The ID of the form to hide error messages for.
     */
    hideErrorForForm(formID) {
        const form = document.getElementById(formID);
        if (form) {
            form.classList.remove('form-has-error');
            this._removeMessages(form, this.formErrorMessageClass); // Remove form-level error messages
            form.querySelectorAll(`.${this.hasErrorClass}`).forEach(item => {
                item.classList.remove(this.hasErrorClass);
                this._removeMessages(item, this.fieldErrorMessageClass); // Remove field-level error messages
            });
            this._removeMessages(form, this.formSuccessMessageClass); // Remove form-level success messages
        }
    }
}
